/**
 * M11: graphics quality and motion preferences. Phones and weak devices automatically get a lighter map
 * (fewer background layers, no per-star glow layers, capped label count, lower render resolution);
 * `prefers-reduced-motion` switches off decorative animation (pulses, twinkle, engine flicker, trails).
 */
export type Quality = 'high' | 'medium' | 'low';

export interface QualityInput { width: number; dpr: number; cores: number; memoryGb?: number }

export function detectQuality(i: QualityInput): Quality {
  if (i.width < 768) return 'low';
  if (i.cores <= 4 || (i.memoryGb !== undefined && i.memoryGb <= 4) || i.width < 1100) return 'medium';
  return 'high';
}

export interface QualitySettings {
  /** world-space nebula clouds */
  nebulae: number;
  /** parallax background layers */
  backgroundLayers: 1 | 2;
  /** max text labels on the map at once */
  maxLabels: number;
  /** render resolution cap */
  maxResolution: number;
  antialias: boolean;
  /** extra glow sprites under owned systems / selected system */
  glowSprites: boolean;
}

export const QUALITY_SETTINGS: Record<Quality, QualitySettings> = {
  high:   { nebulae: 26, backgroundLayers: 2, maxLabels: 140, maxResolution: 2, antialias: true,  glowSprites: true },
  medium: { nebulae: 12, backgroundLayers: 2, maxLabels: 90,  maxResolution: 1.5, antialias: true,  glowSprites: true },
  low:    { nebulae: 5,  backgroundLayers: 1, maxLabels: 50,  maxResolution: 1.25, antialias: false, glowSprites: false },
};

/** Track 2: manual quality choice ('auto' = detect), kept per browser. */
export const QUALITY_PREF_KEY = 'galaxis.quality.v1';
export type QualityPref = Quality | 'auto';

export function getQualityPref(storage: Pick<Storage, 'getItem'> | null = browserStorage()): QualityPref {
  try {
    const v = storage?.getItem(QUALITY_PREF_KEY);
    return v === 'low' || v === 'medium' || v === 'high' ? v : 'auto';
  } catch { return 'auto'; }
}

export function setQualityPref(pref: QualityPref): void {
  try { const s = browserStorage(); if (pref === 'auto') s?.removeItem(QUALITY_PREF_KEY); else s?.setItem(QUALITY_PREF_KEY, pref); } catch { /* storage unavailable */ }
}

/** A stored manual choice wins over the detected quality. */
export function resolveQuality(detected: Quality, storage: Pick<Storage, 'getItem'> | null = browserStorage()): Quality {
  const pref = getQualityPref(storage);
  return pref === 'auto' ? detected : pref;
}

function browserStorage(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

export function currentQuality(): Quality {
  if (typeof window === 'undefined') return 'medium';
  const override = new URLSearchParams(window.location.search).get('quality');
  if (override === 'low' || override === 'medium' || override === 'high') return override;
  const nav = navigator as Navigator & { deviceMemory?: number };
  return resolveQuality(detectQuality({ width: window.innerWidth, dpr: window.devicePixelRatio || 1, cores: navigator.hardwareConcurrency || 4, memoryGb: nav.deviceMemory }));
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
